import { useMemo } from 'react';

import { Box } from 'components/Box/Box';
import { Icon } from 'components/Icon/Icon';
import type { IconName } from 'components/Icon/IconName';
import { LegacyTooltip } from 'components/LegacyTooltip/LegacyTooltip';
import { HeadingSmall } from 'components/Text/HeadingSmall';
import type { ApiOrgType } from 'modules/listing/api/org/types/ApiOrgType';
import { colors } from 'theme/theme';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from 'utils/constants/general/listingTypeHumanNameMapTitle';
import { ORG_TYPES, OrgType } from 'utils/constants/general/orgTypes';

import { getListingTypeLabelIcon } from './getListingTypeLabelIcon';

type Props = {
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT' | 'IMPORTED' | 'ORG';
  orgType?: ApiOrgType;
};

export function ListingTypeLabel({ listingType, orgType }: Props) {
  const icon = getListingTypeLabelIcon({
    listingType,
  });

  const orgTypeIcon: IconName | undefined = useMemo(() => {
    if (!orgType) return undefined;

    switch (orgType) {
      case OrgType.NONPROFIT:
        return 'hand';
      case OrgType.CONSULTANT:
        return 'percentage-award';
      case OrgType.RECRUITER:
        return 'person-add';
      case OrgType.CORPORATION:
        return 'mountain-flag';
      case OrgType.GOVERNMENT:
        return 'government';
      case OrgType.COMMUNITY:
        return 'group';
    }
  }, [orgType]);

  const orgTypeTitle = useMemo(() => {
    if (!orgType) return undefined;

    return ORG_TYPES[orgType] || LISTING_TYPE_HUMAN_NAME_MAP_TITLE.ORG;
  }, [orgType]);

  const content = useMemo(() => {
    if (orgTypeTitle) return orgTypeTitle;

    // TODO: check if we can remove the IMPORTED listing type
    // if we can, add it to the translation list
    if (listingType === 'IMPORTED') return 'imported';

    return LISTING_TYPE_HUMAN_NAME_MAP_TITLE[listingType];
  }, [listingType, orgTypeTitle]);

  const tooltipText = useMemo(() => {
    if (
      !orgType ||
      (orgType && !['CORPORATION', 'COMMUNITY'].includes(orgType))
    )
      return null;

    if (orgType === 'CORPORATION') {
      return getText(
        /* TRANSLATORS: Tooltip next to 'Business' label in organization type when creating new non-501c organization */

        `Social enterprises and approved for-profit companies are allowed to post jobs on Idealist that adhere to our guidelines.`,
      );
    }

    if (orgType === 'COMMUNITY') {
      return getText(
        /* TRANSLATORS: Tooltip next to 'Community Group' label in organization type when creating new non-501c organization */

        'Common interest and other mission-driven, noncommercial groups may now post listings that adhere to the Idealist guidelines.',
      );
    }
  }, [orgType]);

  return (
    <Box display="flex" alignItems="center">
      <Box mr="8px" color={colors.lightContentGrey} display="inline-flex">
        <Icon name={orgTypeIcon || icon} size={19} />
      </Box>

      <HeadingSmall
        data-qa-id="listing-type"
        my={0}
        color={colors.lightContentGrey}
      >
        {content}
      </HeadingSmall>

      {tooltipText && (
        <Box ml="4px" mt="-1px">
          <LegacyTooltip qaId="listing-type-tooltip">
            {tooltipText}
          </LegacyTooltip>
        </Box>
      )}
    </Box>
  );
}
